import React from 'react';
import { AlertTriangle, Check, CheckCircle2, Clock, Users } from 'lucide-react';
import { PulsePanel, PulseEmpty } from './PulsePanel';
import { pctColor, useFitPaged, PageDots } from './pulseUtils';

const STATUS = {
  Completed: { color: '#1e7d3a', bg: '#e3f6e8', icon: CheckCircle2, solid: '#22B04B' },
  'On Track': { color: '#1e7d3a', bg: '#e3f6e8', icon: Check, solid: '#22B04B' },
  'Needs Attention': { color: '#8a5a00', bg: '#fff4d6', icon: AlertTriangle, solid: '#F5B400' },
  Overdue: { color: '#c81e45', bg: '#ffe4ea', icon: Clock, solid: '#EF3A5D' },
};
const COLS = 'grid-cols-[180px_minmax(0,1fr)_58px_repeat(5,40px)_138px_58px]';

const Summary = ({ counts }) => (
  <div className="grid grid-cols-4 gap-3 px-4 pt-4">
    {Object.entries(STATUS).map(([name, s]) => {
      const Icon = s.icon;
      return (
        <div key={name} className="rounded-xl flex items-center gap-3 px-3 py-2.5" style={{ background: s.bg }} data-testid={`pulse-review-summary-${name.toLowerCase().replace(' ', '-')}`}>
          <span className="w-[46px] h-[46px] rounded-full flex items-center justify-center shrink-0" style={{ background: s.solid }}><Icon size={26} color="#fff" strokeWidth={3} /></span>
          <div className="leading-tight"><div className="text-[15px] font-bold whitespace-nowrap">{name}</div>
            <div className="text-[30px] font-extrabold leading-none">{counts[name].count}</div><div className="text-[15px] font-semibold">{counts[name].percent}%</div></div>
        </div>
      );
    })}
  </div>
);

const Row = ({ r }) => {
  const s = STATUS[r.status];
  return (
    <div className={`grid ${COLS} items-center py-[6px] text-[17px] border-b border-[#f3eff4]`} data-testid="pulse-review-row">
      <span className="truncate pr-2 font-medium">{r.name}</span>
      <div className="h-[22px] bg-[#e7e3e9] rounded mr-3 overflow-hidden"><div className="h-full rounded pulse-bar-fill" style={{ width: `${r.percent}%`, background: pctColor(r.percent) }} /></div>
      <span className="font-bold tabular-nums">{r.percent}%</span>
      {r.days.map((on, i) => (
        <span key={i} className="flex justify-center" data-testid={`pulse-review-day-${i}-${on ? 'on' : 'off'}`}>
          {on ? <span className="w-[26px] h-[26px] rounded-full bg-[#22B04B] flex items-center justify-center"><Check size={17} color="#fff" strokeWidth={3.5} /></span>
            : <span className="w-[26px] h-[26px] rounded-full border-2 border-[#cfc8d3]" />}
        </span>
      ))}
      <span className="flex justify-center"><span className="rounded-md px-2.5 py-0.5 text-[13px] font-bold whitespace-nowrap" style={{ background: s.bg, color: s.color }}>{r.status}</span></span>
      <span className="text-right tabular-nums">{r.due}</span>
    </div>
  );
};

export const PulseCaseload = ({ data, rotateSeconds }) => {
  const { ref, slice, page, pages } = useFitPaged(data.rows, (rotateSeconds || 8) * 1000);
  return (
    <PulsePanel icon={Users} title="Caseload Review" subtitle="(Due by Friday)" pill="This Week" testid="pulse-caseload-review" className="h-full">
      <Summary counts={data.counts} />
      <div className="px-4 pt-3 flex-1 min-h-0 flex flex-col">
        <div className={`grid ${COLS} text-[16px] font-bold border-b border-[#ece6ee] pb-1.5`}>
          <span>Case Manager</span><span className="whitespace-nowrap">Completion Progress</span><span />
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((d) => <span key={d} className="text-center">{d}</span>)}
          <span className="text-center">Status</span><span className="text-right">Due</span>
        </div>
        {data.rows.length === 0 ? <PulseEmpty text="No caseload reviews assigned for this week yet" testid="pulse-review-empty" />
          : <div key={page} ref={ref} className="flex-1 min-h-0 overflow-hidden pulse-fade-in">{slice.map((r) => <Row key={r.name} r={r} />)}</div>}
        <PageDots page={page} pages={pages} testid="pulse-review-pages" />
      </div>
    </PulsePanel>
  );
};
